import { Container, Sprite, Texture } from "pixi.js"
import { TreeArt, fallbackTreeArt } from "./tree_art"

/**
 * What a tree does when something hits it.
 *
 * This replaced a 25Hz sideways jitter of the whole sprite, 3-6px for ~150ms. Three things were wrong
 * with that, and each of the parts below answers one of them:
 *
 *   THE GROUND MOVED. Every pixel of the tree slid by the same amount, roots and shadow included, so
 *   the tree read as a decal being nudged rather than as something rooted being struck. The RECOIL
 *   here is a shear about the foot of the art -- the roots stay planted and the crown carries the whole
 *   swing -- using the same "skew about the top, slide back by the base's travel" pair GrassSway uses,
 *   so a tree split across several sprites still bends as one piece.
 *
 *   IT WAS A VIBRATION, NOT A BLOW. 25Hz squeezed into nine frames aliases into a buzz. The recoil is a
 *   damped spring kicked by an impulse AWAY from whoever struck it: the crown lurches over inside the
 *   first ~80ms, swings back past upright and settles over the better part of a second, about three
 *   tenths of a second a swing. Blows that land while it is still swinging add to the motion they
 *   land on, so a run of axe strokes works the tree rather than restarting a canned animation.
 *
 *   NOTHING CAME OFF THE TREE. A hit now knocks CHIPS out of the trunk where the axe bit, toward the
 *   swing -- pale fresh wood and the tree's own bark, arcing out, bouncing once and lying in the dirt --
 *   with a small puff of bark dust, and shakes LEAVES loose from the canopy that flutter down in front
 *   of it. Their colours are read off the tree's own art (tree_art.ts), so a blossom tree drops petals.
 *
 * Everything scales with how hard the blow was: an axe stroke is 1, a fist is a third of that, a
 * warhammer half again.
 *
 * NO TIMERS. One update() off the render loop advances every recoil and every particle (the old shake
 * held a setInterval per struck tile). The particles are ordinary z-sorted children of the world
 * surface, so they interleave with bodies the way FootDust's puffs do, and never sort behind the tree
 * they came off.
 *
 * LIVE TUNING, from the browser console: window.treeHit.{enabled, lean, frequency, damping, leaves, chips}
 */

export interface TreeHitTextures {
    leaves: Texture[]
    puffs: Texture[]
}

export interface TreeStrike {
    // Identifies the tree across frames; a second blow on the same key adds to the swing in progress.
    key: string
    // The tree's CURRENT sprites. Re-read every frame rather than held: a full map rerender hands the
    // tree new pooled sprites mid-swing, and the old ones may by then be some other tile.
    sprites: () => Sprite[] | undefined
    // What the art looks like (where the trunk is, its colours). Undefined: a generic tree shape.
    art?: TreeArt
    // World position of whoever struck it. Undefined: a random side.
    from?: { x: number; y: number }
    // 1 = an axe blow.
    strength: number
}

export interface TreeHitTuning {
    enabled: boolean
    // Peak lean of an axe blow: px of sway per px of height above the roots. 0.032 puts the top of a
    // five-tile pine about five pixels over.
    lean: number
    // The sway's natural frequency in Hz, and its damping ratio. ~3Hz and 0.2 is about three visible
    // swings, mostly settled by 0.6s: springy, but done before the next stroke of a steady chop lands.
    frequency: number
    damping: number
    // Leaves shaken loose, and chips knocked out, by one axe blow.
    leaves: number
    chips: number
}

interface Base {
    // Where the map put the sprite, and what this effect last wrote over it. A sprite whose x is no
    // longer what was written has been repositioned by the map (a rerender) and is re-based.
    x: number
    written: number
}

interface Recoil {
    footOffset: number
    sprites: () => Sprite[] | undefined
    // The lean, as px of horizontal travel per px of height (positive = top to the right), and its rate.
    angle: number
    velocity: number
    bases: Map<Sprite, Base>
}

const enum Kind {
    Chip,
    Leaf,
    Puff,
}

interface Bit {
    kind: Kind
    sprite: Sprite
    // Ground position and height above it, drawn at (x, groundY - z) and sorted on groundY: a chip that
    // flies up must not change which bodies it sorts behind.
    x: number
    groundY: number
    z: number
    vx: number
    vy: number
    vz: number
    // Never sort behind the tree it came off: leaves fall down its FRONT.
    zFloor: number
    spin: number
    scale: number
    alpha: number
    bornMs: number
    // 0 while airborne.
    landedMs: number
    restMs: number
    bounces: number
    // Leaf flight.
    terminal: number
    swayAmp: number
    swayFreq: number
    swayPhase: number
    flutterFreq: number
    flutterPhase: number
    // Puff life.
    lifeMs: number
    growth: number
}

// Integration step for the spring: semi-implicit Euler is comfortably stable at 4ms for a 3Hz spring,
// and a long frame is taken in several of these rather than one big step.
const STEP = 1 / 240
// A frame longer than this is a stall, not motion.
const MAX_FRAME_SECONDS = 0.05
// Hard limit on the lean, however many blows stack up: about four degrees.
const MAX_LEAN = 0.075
// Below both of these the tree is upright and still, and is handed back to the map untouched.
const REST_ANGLE = 0.0004
const REST_VELOCITY = 0.01

// Same convention as FootDust: something lying on the ground at groundY sorts like a body whose soles
// are there, and a body's sort line is its hip, this far above its soles.
const FOOT_DROP = 20

const CHIP_GRAVITY = 480
const CHIP_SPEED_MIN = 35
const CHIP_SPEED_MAX = 95
const CHIP_REST_MS = [450, 950]
const LEAF_GRAVITY = 160
const LEAF_DRAG = 2.4
const LEAF_REST_MS = [600, 1300]
const FADE_MS = 380
const PUFF_LIFE_MS = 420

// What the inside of a tree looks like: most chips are this, not bark. A chip of bark alone reads as
// dirt; the pale ones are what say "wood".
const FRESH_WOOD = [0xe7cb98, 0xd8b37c, 0xc99c63]
const BARK_DUST = 0xd6c2a0

// Total live particles. A crowd of woodcutters is the case this is for -- the particles are garnish,
// and dropping the newest is invisible where a frame spent on hundreds of sprites is not.
const MAX_BITS = 140

const between = (min: number, max: number) => min + Math.random() * (max - min)
const pick = <T>(list: T[]): T => list[Math.floor(Math.random() * list.length)]
const alive = (s: Sprite | undefined): s is Sprite => !!s && !s.destroyed && !!s.transform && !!s.parent
const spriteHeight = (s: Sprite) => s.texture.height * Math.abs(s.scale.y)

// Peak lean reached by a spring at rest kicked with unit velocity: the impulse is sized from this, so
// `lean` means the peak whatever the frequency and damping are tuned to.
const peakPerUnitVelocity = (omega: number, zeta: number) => {
    const damped = omega * Math.sqrt(Math.max(0.01, 1 - zeta * zeta))
    const tPeak = Math.atan2(damped, zeta * omega) / damped
    return (Math.exp(-zeta * omega * tPeak) * Math.sin(damped * tPeak)) / damped
}

export class TreeHit {
    readonly tuning: TreeHitTuning = {
        enabled: true,
        lean: 0.032,
        frequency: 3.1,
        damping: 0.2,
        leaves: 5,
        chips: 5,
    }

    private surface: Container
    private makeTextures: () => TreeHitTextures
    private textures: TreeHitTextures | undefined
    private recoils = new Map<string, Recoil>()
    private bits: Bit[] = []
    private pool: Sprite[] = []
    private lastMs?: number

    setSurface(surface: Container): void {
        this.surface = surface
    }

    constructor(surface: Container, makeTextures: () => TreeHitTextures) {
        this.surface = surface
        this.makeTextures = makeTextures
        if (typeof window !== "undefined") {
            ;(window as any).treeHit = this.tuning
        }
    }

    // Exposed for tests.
    isSwaying = (key: string) => this.recoils.has(key)
    leanOf = (key: string) => this.recoils.get(key)?.angle ?? 0
    particleCount = () => this.bits.length

    /** Hit a tree. Returns false when there was nothing on screen to hit. */
    strike = (strike: TreeStrike, nowMs: number = performance.now()): boolean => {
        if (!this.tuning.enabled) return false
        const set = (strike.sprites() || []).filter(alive)
        if (!set.length) return false
        this.ensureTextures()

        let recoil = this.recoils.get(strike.key)
        if (!recoil) {
            recoil = { footOffset: 0, sprites: strike.sprites, angle: 0, velocity: 0, bases: new Map() }
            this.recoils.set(strike.key, recoil)
        }
        recoil.sprites = strike.sprites

        // Where the art is in the world, measured from where the map PUT the sprites, not where the
        // swing has them this frame.
        let left = Infinity
        let top = Infinity
        let right = -Infinity
        let bottom = -Infinity
        let treeZ = -Infinity
        for (const s of set) {
            const x = this.baseX(recoil, s)
            left = Math.min(left, x)
            top = Math.min(top, s.y)
            right = Math.max(right, x + s.texture.width * Math.abs(s.scale.x))
            bottom = Math.max(bottom, s.y + spriteHeight(s))
            treeZ = Math.max(treeZ, s.zIndex)
        }
        const width = right - left
        const height = bottom - top
        // The analysed art describes the whole composite; a set that is not that size is not that art.
        const art = strike.art && Math.abs(strike.art.width - width) <= 1 && Math.abs(strike.art.height - height) <= 1
            ? strike.art
            : fallbackTreeArt(width, height)

        recoil.footOffset = art === strike.art ? height - art.footY : 0

        const trunkX = left + (art.trunkLeft + art.trunkRight + 1) / 2
        const dx = strike.from ? trunkX - strike.from.x : 0
        // Knocked AWAY from the blow. Struck from dead ahead (or by nothing in particular), either way.
        const away = Math.abs(dx) >= 4 ? Math.sign(dx) : Math.random() < 0.5 ? -1 : 1
        const strength = Math.max(0.2, Math.min(2, strike.strength || 1))

        const omega = 2 * Math.PI * this.tuning.frequency
        recoil.velocity += (away * strength * this.tuning.lean) / peakPerUnitVelocity(omega, this.tuning.damping)

        const scene = {
            left,
            top,
            pivot: bottom,
            footY: top + art.footY,
            trunkX,
            halfTrunk: (art.trunkRight - art.trunkLeft + 1) / 2,
            impactHeight: Math.max(4, art.footY - art.impactY),
            zFloor: treeZ + 0.5,
            lean: recoil.angle,
        }
        // Chips fly back toward the swing; from dead ahead they spray both ways.
        this.spawnChips(scene, art, Math.abs(dx) >= 6 ? -away : 0, strength, nowMs)
        this.spawnLeaves(scene, art, away, strength, nowMs)
        return true
    }

    /** Advance every swing and every particle. Called once per drawn frame. */
    update = (nowMs: number = performance.now()) => {
        if (!this.tuning.enabled) { this.clear(); return }
        if (this.lastMs === undefined) {
            this.lastMs = nowMs
            return
        }
        const dt = Math.min(Math.max(0, (nowMs - this.lastMs) / 1000), MAX_FRAME_SECONDS)
        this.lastMs = nowMs
        if (this.recoils.size) this.advanceRecoils(dt)
        if (this.bits.length) this.advanceBits(nowMs, dt)
    }

    /** Stand every tree up and sweep the particles away (a new room). */
    clear = () => {
        this.recoils.forEach(recoil => this.straighten(recoil, (recoil.sprites() || []).filter(alive)))
        this.recoils.clear()
        this.bits.forEach(this.retire)
        this.bits = []
        this.lastMs = undefined
    }

    releaseSprites = () => {
        this.recoils.forEach(recoil => {
            this.straighten(recoil, (recoil.sprites() || []).filter(alive))
            recoil.bases.clear()
        })
    }

    // ---------------------------------------------------------------------------------------------
    // The swing
    // ---------------------------------------------------------------------------------------------

    private advanceRecoils = (dt: number) => {
        const omega = 2 * Math.PI * this.tuning.frequency
        const zeta = this.tuning.damping
        this.recoils.forEach((recoil, key) => {
            for (let left = dt; left > 1e-6; left -= STEP) {
                const h = Math.min(STEP, left)
                recoil.velocity += (-omega * omega * recoil.angle - 2 * zeta * omega * recoil.velocity) * h
                recoil.angle += recoil.velocity * h
            }
            recoil.angle = Math.max(-MAX_LEAN, Math.min(MAX_LEAN, recoil.angle))

            const set = (recoil.sprites() || []).filter(alive)
            if (!set.length) {
                // Felled, or the room went away. Nothing of ours is left on screen to put back.
                this.recoils.delete(key)
                return
            }
            if (Math.abs(recoil.angle) < REST_ANGLE && Math.abs(recoil.velocity) < REST_VELOCITY) {
                this.straighten(recoil, set)
                this.recoils.delete(key)
                return
            }
            const current = new Set(set)
            recoil.bases.forEach((_, sprite) => { if (!current.has(sprite)) recoil.bases.delete(sprite) })
            this.lean(recoil, set)
        })
    }

    // Shear every piece of the tree about the foot of the art. Each piece's top and bottom edge land
    // where the lean puts that height, so the pieces of a split tree meet exactly; for the usual single
    // sprite this is just a lean with the roots pinned. asin rather than the small-angle shortcut for
    // the same reason GrassSway gives: at the world's 2x zoom a fraction of a pixel shows as a seam.
    private lean = (recoil: Recoil, set: Sprite[]) => {
        let pivot = -Infinity
        for (const s of set) pivot = Math.max(pivot, s.y + spriteHeight(s))
        pivot -= recoil.footOffset
        for (const s of set) {
            const h = spriteHeight(s)
            if (h <= 0) continue
            let base = recoil.bases.get(s)
            if (!base || s.x !== base.written) {
                base = { x: s.x, written: s.x }
                recoil.bases.set(s, base)
            }
            const top = recoil.angle * (pivot - s.y)
            const bottom = recoil.angle * (pivot - s.y - h)
            s.skew.x = Math.asin(Math.max(-1, Math.min(1, (bottom - top) / h)))
            s.x = base.x + top
            base.written = s.x
        }
    }

    // Hand the sprites back exactly as the map left them -- but only the ones still ours: a sprite whose
    // x is not what this effect last wrote has been re-placed by the map and is not touched.
    private straighten = (recoil: Recoil, set: Sprite[]) => {
        for (const s of set) {
            const base = recoil.bases.get(s)
            if (base && s.x === base.written) {
                s.x = base.x
                s.skew.x = 0
            }
        }
    }

    private baseX = (recoil: Recoil, s: Sprite) => {
        const base = recoil.bases.get(s)
        return base && s.x === base.written ? base.x : s.x
    }

    // ---------------------------------------------------------------------------------------------
    // What comes off the tree
    // ---------------------------------------------------------------------------------------------

    private spawnChips = (scene: Scene, art: TreeArt, toward: number, strength: number, nowMs: number) => {
        const count = Math.round(this.tuning.chips * Math.min(strength, 1.5) * between(0.8, 1.2))
        const bark = art.barkColors.length ? art.barkColors : FRESH_WOOD
        // Where the axe bit, on the face toward the swing, carried by the lean at that height.
        const lift = scene.lean * scene.impactHeight
        for (let i = 0; i < count; i++) {
            const side = toward || (Math.random() < 0.5 ? -1 : 1)
            const sprite = this.add(Texture.WHITE)
            if (!sprite) return
            const w = between(1.5, 3)
            const h = between(1, 1.8)
            sprite.anchor.set(0.5)
            sprite.scale.set(w / Texture.WHITE.width, h / Texture.WHITE.height)
            sprite.rotation = Math.random() * Math.PI
            sprite.tint = Math.random() < 0.6 ? pick(FRESH_WOOD) : pick(bark)
            const speed = between(CHIP_SPEED_MIN, CHIP_SPEED_MAX) * (0.7 + 0.3 * Math.min(strength, 1.5))
            this.push({
                ...this.blank(Kind.Chip, sprite, nowMs, scene.zFloor),
                x: scene.trunkX + side * Math.max(1, scene.halfTrunk - 1) + lift,
                groundY: scene.footY + between(0, 3),
                z: scene.impactHeight,
                vx: side * speed,
                // Struck from dead ahead, chips come out toward the viewer as much as sideways.
                vy: toward ? between(-10, 26) : between(10, 40),
                vz: between(45, 110),
                spin: (Math.random() < 0.5 ? -1 : 1) * between(8, 20),
                restMs: between(CHIP_REST_MS[0], CHIP_REST_MS[1]),
            })
        }

        // A breath of bark dust off the cut, on the side the axe came from.
        const puffs = this.textures?.puffs || []
        if (puffs.length && count > 0) {
            const side = toward || (Math.random() < 0.5 ? -1 : 1)
            for (let i = 0; i < (strength >= 1 ? 2 : 1); i++) {
                const sprite = this.add(pick(puffs))
                if (!sprite) return
                sprite.anchor.set(0.5, 0.7)
                sprite.tint = BARK_DUST
                sprite.rotation = Math.random() * Math.PI * 2
                const scale = between(0.26, 0.38)
                this.push({
                    ...this.blank(Kind.Puff, sprite, nowMs, scene.zFloor),
                    x: scene.trunkX + side * scene.halfTrunk + lift,
                    groundY: scene.footY + 1,
                    z: scene.impactHeight - 2,
                    vx: side * between(10, 26),
                    vz: between(6, 14),
                    scale,
                    alpha: between(0.4, 0.6),
                    lifeMs: PUFF_LIFE_MS * between(0.8, 1.2),
                    growth: between(1.6, 2.1),
                    spin: between(-0.8, 0.8),
                })
            }
        }
    }

    private spawnLeaves = (scene: Scene, art: TreeArt, away: number, strength: number, nowMs: number) => {
        const leaves = this.textures?.leaves || []
        const points = art.canopyPoints
        if (!leaves.length || points.length < 2) return
        const colors = art.canopyColors.length ? art.canopyColors : [0x548c3a]
        const count = Math.max(0, Math.round(this.tuning.leaves * Math.min(strength, 1.5) * between(0.7, 1.3)))
        for (let i = 0; i < count; i++) {
            const p = 2 * Math.floor(Math.random() * (points.length / 2))
            const worldY = scene.top + points[p + 1]
            // Shaken off where the canopy IS this frame, mid-lean.
            const worldX = scene.left + points[p] + scene.lean * (scene.pivot - worldY)
            // Leaves land around the foot of the tree, in front of it: the canopy overhangs the ground
            // there as much as anywhere, and a leaf landing behind the trunk would be hidden by it.
            const groundY = scene.footY + between(-2, 12)
            const sprite = this.add(pick(leaves))
            if (!sprite) return
            const scale = between(0.26, 0.4)
            sprite.anchor.set(0.5)
            sprite.scale.set(scale)
            sprite.rotation = Math.random() * Math.PI * 2
            sprite.tint = pick(colors)
            this.push({
                ...this.blank(Kind.Leaf, sprite, nowMs, scene.zFloor),
                x: worldX,
                groundY,
                z: Math.max(6, groundY - worldY),
                // Flung the way the crown lurched, then the air takes over.
                vx: away * between(10, 32) * Math.min(strength, 1.5) + between(-6, 6),
                vy: between(-2, 6),
                vz: between(0, 18),
                scale,
                terminal: between(40, 62),
                swayAmp: between(10, 22),
                swayFreq: between(2.2, 4),
                swayPhase: Math.random() * Math.PI * 2,
                flutterFreq: between(5, 10),
                flutterPhase: Math.random() * Math.PI * 2,
                spin: (Math.random() < 0.5 ? -1 : 1) * between(1.5, 4.5),
                restMs: between(LEAF_REST_MS[0], LEAF_REST_MS[1]),
            })
        }
    }

    private advanceBits = (nowMs: number, dt: number) => {
        const leafDrag = Math.exp(-LEAF_DRAG * dt)
        let write = 0
        for (let i = 0; i < this.bits.length; i++) {
            const bit = this.bits[i]
            const sprite = bit.sprite
            if (sprite.destroyed) continue

            if (bit.kind === Kind.Puff) {
                const age = (nowMs - bit.bornMs) / bit.lifeMs
                if (age >= 1) {
                    this.retire(bit)
                    continue
                }
                bit.x += bit.vx * dt
                bit.z += bit.vz * dt
                bit.vx *= Math.exp(-5 * dt)
                bit.vz *= Math.exp(-3 * dt)
                sprite.rotation += bit.spin * dt
                const eased = 1 - (1 - age) * (1 - age)
                sprite.scale.set(bit.scale * (1 + (bit.growth - 1) * eased))
                sprite.alpha = bit.alpha * (age < 0.15 ? age / 0.15 : 1 - ((age - 0.15) / 0.85) ** 2)
            } else if (!bit.landedMs) {
                if (bit.kind === Kind.Chip) {
                    bit.vz -= CHIP_GRAVITY * dt
                    bit.x += bit.vx * dt
                    bit.groundY += bit.vy * dt
                    bit.z += bit.vz * dt
                    sprite.rotation += bit.spin * dt
                    if (bit.z <= 0) {
                        bit.z = 0
                        if (bit.bounces < 1 && bit.vz < -40) {
                            // One hop, much smaller, and most of the slide is gone.
                            bit.vz = -bit.vz * 0.32
                            bit.vx *= 0.45
                            bit.vy *= 0.45
                            bit.spin *= 0.5
                            bit.bounces++
                        } else {
                            bit.landedMs = nowMs
                        }
                    }
                } else {
                    bit.vz = Math.max(bit.vz - LEAF_GRAVITY * dt, -bit.terminal)
                    bit.vx *= leafDrag
                    bit.vy *= leafDrag
                    const t = (nowMs - bit.bornMs) / 1000
                    bit.x += (bit.vx + bit.swayAmp * Math.sin(t * bit.swayFreq + bit.swayPhase)) * dt
                    bit.groundY += bit.vy * dt
                    bit.z += bit.vz * dt
                    sprite.rotation += bit.spin * dt
                    // Turning edge-on and back as it tumbles.
                    const flutter = Math.abs(Math.cos(t * bit.flutterFreq + bit.flutterPhase))
                    sprite.scale.set(bit.scale, bit.scale * (0.3 + 0.7 * flutter))
                    if (bit.z <= 0) {
                        bit.z = 0
                        bit.landedMs = nowMs
                        // Lying flat, seen at the camera's angle.
                        sprite.scale.set(bit.scale, bit.scale * 0.55)
                    }
                }
            } else {
                const lying = nowMs - bit.landedMs
                if (lying >= bit.restMs + FADE_MS) {
                    this.retire(bit)
                    continue
                }
                sprite.alpha = lying > bit.restMs ? bit.alpha * (1 - (lying - bit.restMs) / FADE_MS) : bit.alpha
            }

            this.draw(bit)
            this.bits[write++] = bit
        }
        this.bits.length = write
    }

    private draw = (bit: Bit) => {
        const sprite = bit.sprite
        sprite.x = bit.x
        sprite.y = bit.groundY - bit.z
        const z = Math.max(100 + bit.groundY - FOOT_DROP, bit.zFloor)
        // zIndex marks the whole world surface for a re-sort; only touch it when it moved.
        if (sprite.zIndex !== z) sprite.zIndex = z
    }

    private add = (texture: Texture): Sprite | undefined => {
        if (this.bits.length >= MAX_BITS) return undefined
        const sprite = this.pool.pop() || new Sprite(texture)
        sprite.texture = texture
        sprite.alpha = 1
        sprite.scale.set(1)
        sprite.skew.set(0)
        sprite.rotation = 0
        this.surface.addChild(sprite)
        return sprite
    }

    private push = (bit: Bit) => {
        this.bits.push(bit)
        this.draw(bit)
    }

    private blank = (kind: Kind, sprite: Sprite, nowMs: number, zFloor: number): Bit => {
        // Built here, on the first particle, not at construction: the renderer may not be able to bake
        // a texture yet, and in a test there is no GPU at all.
        this.ensureTextures()
        return {
            kind,
            sprite,
            x: 0,
            groundY: 0,
            z: 0,
            vx: 0,
            vy: 0,
            vz: 0,
            zFloor,
            spin: 0,
            scale: 1,
            alpha: 1,
            bornMs: nowMs,
            landedMs: 0,
            restMs: 0,
            bounces: 0,
            terminal: 0,
            swayAmp: 0,
            swayFreq: 0,
            swayPhase: 0,
            flutterFreq: 0,
            flutterPhase: 0,
            lifeMs: 0,
            growth: 1,
        }
    }

    private ensureTextures = () => {
        if (this.textures) return
        try {
            this.textures = this.makeTextures()
        } catch (e) {
            this.textures = { leaves: [], puffs: [] }
        }
    }

    private retire = (bit: Bit) => {
        bit.sprite.parent?.removeChild(bit.sprite)
        if (!bit.sprite.destroyed) {
            if (this.pool.length < MAX_BITS) this.pool.push(bit.sprite)
            else bit.sprite.destroy()
        }
    }
}

interface Scene {
    // Top-left of the art in the world, and the line it pivots on (the bottom of the art).
    left: number
    top: number
    pivot: number
    // Where the roots meet the ground, the trunk's middle, and how high on it the axe landed.
    footY: number
    trunkX: number
    halfTrunk: number
    impactHeight: number
    zFloor: number
    // The tree's lean at the moment of the blow.
    lean: number
}
